/**
 * AuthProvider pour Refine utilisant Keycloak OAuth2
 * Remplace l'authentification user-service HMAC par Keycloak OAuth2
 */

import { AuthProvider } from '@refinedev/core';
import {
  initiateLogin,
  logout as keycloakLogout,
  getAccessToken,
  getUserFromToken,
  isAuthenticated,
} from './keycloak';
import { hasBackofficeAccess } from '@/services/privileges';

// La liste des rôles et le contrôle d'accès vivent desormais dans un seul
// fichier, partagé avec le menu : deux listes auraient fini par diverger.
// « SUPER_ADMIN » y figurait alors que ce rôle n'existe pas dans le realm.

export const authKeycloakProvider: AuthProvider = {
  /**
   * Login: Redirige vers Keycloak pour authentification OAuth2
   */
  login: async () => {
    try {
      // Initier le flow OAuth2 Authorization Code + PKCE
      await initiateLogin();

      // La fonction initiateLogin() redirige vers Keycloak
      // Le retour se fait via /auth/callback

      return {
        success: true,
        // Pas de redirectTo car la redirection est gérée par Keycloak
      };
    } catch (error: any) {
      console.error('Login initiation failed:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Erreur lors de l\'initialisation de la connexion',
          name: 'LoginError',
        },
      };
    }
  },

  /**
   * Logout: Déconnexion Keycloak et nettoyage local
   */
  logout: async () => {
    try {
      await keycloakLogout();

      // keycloakLogout() redirige vers Keycloak puis vers /login
      return {
        success: true,
        redirectTo: '/login',
      };
    } catch (error: any) {
      console.error('Logout failed:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Erreur lors de la déconnexion',
          name: 'LogoutError',
        },
      };
    }
  },

  /**
   * Check: Vérifie si l'utilisateur est authentifié
   * Appelé automatiquement par Refine avant chaque route protégée
   */
  check: async () => {
    try {
      const authenticated = await isAuthenticated();

      if (authenticated) {
        // Garde de rôles : seuls ADMIN, AUTHOR, REVIEWER (+ SUPER_ADMIN) accèdent au backoffice.
        const user = getUserFromToken();
        if (!hasBackofficeAccess(user?.roles)) {
          return {
            authenticated: false,
            error: {
              message: "Accès refusé : votre compte n'a pas les droits nécessaires pour le backoffice.",
              name: 'Forbidden',
            },
            logout: true,
            redirectTo: '/login',
          };
        }

        return {
          authenticated: true,
        };
      }

      return {
        authenticated: false,
        error: {
          message: 'Session expirée, veuillez vous reconnecter',
          name: 'Unauthenticated',
        },
        logout: true,
        redirectTo: '/login',
      };
    } catch (error: any) {
      console.error('Auth check failed:', error);
      return {
        authenticated: false,
        error: {
          message: 'Erreur de vérification d\'authentification',
          name: 'AuthCheckError',
        },
        logout: true,
        redirectTo: '/login',
      };
    }
  },

  /**
   * GetPermissions: Récupère les rôles de l'utilisateur
   * Utilisé par Refine pour le contrôle d'accès basé sur les rôles
   */
  getPermissions: async () => {
    try {
      const user = getUserFromToken();
      if (user && user.roles) {
        return user.roles;
      }
      return [];
    } catch (error) {
      console.error('Failed to get permissions:', error);
      return [];
    }
  },

  /**
   * GetIdentity: Récupère les informations de l'utilisateur connecté
   * Utilisé par Refine pour afficher le profil utilisateur
   */
  getIdentity: async () => {
    try {
      const user = getUserFromToken();
      if (user) {
        return {
          id: user.id,
          name: user.username,
          username: user.username,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          fullName: user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.username,
          roles: user.roles,
          avatar: user.email
            ? `https://www.gravatar.com/avatar/${hashEmail(user.email)}?d=identicon`
            : undefined,
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to get identity:', error);
      return null;
    }
  },

  /**
   * OnError: Gère les erreurs d'authentification
   * Appelé automatiquement par Refine lors d'erreurs API
   */
  onError: async (error: any) => {
    console.error('Auth Error:', error);

    // Si erreur 401, forcer la déconnexion
    if (error?.status === 401 || error?.statusCode === 401) {
      // Nettoyer les tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('id_token');
      localStorage.removeItem('user');

      return {
        logout: true,
        redirectTo: '/login',
        error: {
          message: 'Session expirée, veuillez vous reconnecter',
          name: 'Unauthorized',
        },
      };
    }

    // Si erreur 403, l'utilisateur n'a pas les permissions
    if (error?.status === 403 || error?.statusCode === 403) {
      return {
        error: {
          message: 'Vous n\'avez pas les permissions nécessaires pour cette action',
          name: 'Forbidden',
        },
      };
    }

    return {};
  },
};

/**
 * Fonction utilitaire pour hacher un email (pour Gravatar)
 */
function hashEmail(email: string): string {
  // Simple hash pour Gravatar (utiliser une vraie librairie crypto en production)
  const trimmed = email.trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

export default authKeycloakProvider;
